'use client';

import { default as NextImage } from 'next/image';

import { buildImageUrl } from '@/lib/config/image-env';
import { cn } from '@/lib/utils/tailwind';

import type { ImageLoader, ImageProps as NextImageProps } from 'next/image';
import type { ReactNode } from 'react';

// ─── Constants ──────────────────────────────────────────────────────────────

export const ASPECT_RATIOS = {
  square: 'aspect-square',
  wide: 'aspect-video',
} as const;

export type AspectRatio = keyof typeof ASPECT_RATIOS;
export type Breakpoint = [number, number];

const DEFAULT_BREAKPOINTS: Breakpoint[] = [
  [600, 150],
  [900, 300],
  [1200, 600],
  [1920, 900],
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildSizes(breakpoints: Breakpoint[]): string {
  return breakpoints
    .map(([media, size]) => `(max-width: ${media}px) ${size}px`)
    .concat('100vw')
    .join(', ');
}

const defaultLoader: ImageLoader = ({ src, width }) =>
  buildImageUrl(src, width);

// ─── Types ────────────────────────────────────────────────────────────────────

export type ImageProps = Omit<NextImageProps, 'loader' | 'alt'> & {
  src: NextImageProps['src'];
  /** Defaults to "Image" when omitted. */
  alt?: string;
  aspectRatio?: AspectRatio;
  /**
   * Responsive size hints passed to the `sizes` attribute.
   * Defaults to a standard 4-tier breakpoint ladder.
   */
  breakpoints?: Breakpoint[];
  loader?: ImageLoader;
  /**
   * Wrapper-fill mode only.
   * className applied to the container <div>.
   */
  wrapperClassName?: string;
  /**
   * Wrapper-fill mode only.
   * Overlay content (e.g. action buttons) rendered inside the container.
   */
  children?: ReactNode;
  /**
   * WYSIWYG mode — renders an inline, auto-sized image with no wrapper.
   * Use inside rich-text content where the image must flow with surrounding text.
   */
  wysiwyg?: boolean;
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Unified image component wrapping Next.js Image with automatic CDN optimisation.
 *
 * **Mode is inferred from the props passed:**
 *
 * | Props                  | Mode                                               |
 * |------------------------|----------------------------------------------------|
 * | `wysiwyg`              | Inline auto-sized image — no wrapper               |
 * | `fill`                 | Raw fill — caller must provide a relative container|
 * | `width` + `height`     | Fixed dimensions                                   |
 * | _(none of the above)_  | Wrapper-fill — component renders its own container |
 */
const Image = ({
  src,
  alt = 'Image',
  aspectRatio,
  className,
  breakpoints = DEFAULT_BREAKPOINTS,
  loader = defaultLoader,
  fill,
  width,
  height,
  unoptimized,
  wysiwyg = false,
  wrapperClassName,
  children,
  ...rest
}: ImageProps) => {
  const sizes = buildSizes(breakpoints);
  const ratioClass = aspectRatio ? ASPECT_RATIOS[aspectRatio] : undefined;
  const isStaticImage = typeof src !== 'string';
  const imageLoader = isStaticImage ? undefined : loader;
  const imageUnoptimized = isStaticImage || unoptimized;

  // ── WYSIWYG ─────────────────────────────────────────────────────────────
  // Uses real dimensions when provided so the <img> HTML attribute is non-zero.
  // Samsung Internet collapses images that have width="0" + width:auto in CSS
  // because it resolves auto against the HTML attribute (0) instead of falling
  // back to the intrinsic size. maxWidth:100% + width:auto is stable once the
  // element has a non-zero intrinsic width from its HTML attributes.
  if (wysiwyg) {
    return (
      <NextImage
        src={src}
        alt={alt}
        width={width ?? 0}
        height={height ?? 0}
        sizes={sizes}
        loader={imageLoader}
        unoptimized={imageUnoptimized}
        className={cn('h-auto', className)}
        style={{ maxWidth: '100%', width: 'auto', height: 'auto' }}
        {...rest}
      />
    );
  }

  // ── Raw fill ─────────────────────────────────────────────────────────────
  // No wrapper — the caller's container must be `position: relative` and sized.
  if (fill) {
    return (
      <NextImage
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        loader={imageLoader}
        unoptimized={imageUnoptimized}
        className={cn('object-cover', className)}
        {...rest}
      />
    );
  }

  // ── Fixed ─────────────────────────────────────────────────────────────────
  // Explicit pixel dimensions — used for logos, thumbnails, and fixed-size artwork.
  if (width !== undefined && height !== undefined) {
    return (
      <NextImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loader={imageLoader}
        unoptimized={imageUnoptimized}
        className={cn(ratioClass, className)}
        {...rest}
      />
    );
  }

  // ── Wrapper-fill (default) ────────────────────────────────────────────────
  // Component owns a relative container that fills its parent.
  // aspectRatio on the wrapper establishes the intrinsic ratio;
  // children can be used for absolute-positioned overlays.
  return (
    <div
      className={cn(
        'relative h-full overflow-hidden',
        ratioClass,
        wrapperClassName,
      )}
    >
      <NextImage
        src={src}
        alt={alt}
        fill
        sizes={sizes}
        loader={imageLoader}
        unoptimized={imageUnoptimized}
        className={cn(
          'object-cover h-full w-full transition-all duration-300 ease-in-out',
          className,
        )}
        {...rest}
      />
      {children}
    </div>
  );
};

Image.displayName = 'Image';

export { Image };
